// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { WeightUnit } from '@seedplanner/shared-utils';
import type React from 'react';
import { getNumericID } from '../../utils';
import type { CropOrBlendToUseFormInput, Props as NewCropOrBlendToUseFormProps } from './NewCropOrBlendToUseForm';

const SIZE_KEY_SEPARATOR = ':';
interface SizeKey {
  readonly size: number | null;
  readonly unit: string;
  readonly pricePerItem: number | null;
  readonly productId: number | null;
}
export const createSizeKey = ({ size, unit, pricePerItem, productId }: SizeKey): string => {
  if (size == null) {
    // If size is null then it means we have only a unit without a size, price, or product ID
    // For example: "ounces" or "grams" or "trays"
    return ['', unit, '', ''].join(SIZE_KEY_SEPARATOR);
  }

  return [size ?? '', unit, pricePerItem ?? '', productId ?? ''].join(SIZE_KEY_SEPARATOR);
};
export const getSizeParamsFromKey = (key: string): SizeKey => {
  const [sizeIn, unit, pricePerItemIn, productId] = key.split(SIZE_KEY_SEPARATOR);

  return {
    size: sizeIn === '' ? null : Number.parseFloat(sizeIn),
    unit,
    pricePerItem: pricePerItemIn === '' ? null : Number.parseFloat(pricePerItemIn),
    productId: productId === '' ? null : Number.parseInt(productId, 10),
  };
};

export function getBestSizeKey({
  currentProducts,
  inputSize,
  inputUnit,
  inputPricePerItem,
  inputProductId,
  preferredWeightUnit,
}: {
  readonly inputSize: number | null;
  readonly inputUnit: string;
  readonly inputPricePerItem: number | null;
  readonly inputProductId: number | null;
  readonly currentProducts: ReadonlyArray<{
    readonly size: number | null;
    readonly unit: string;
    readonly pricePerItem: number | null;
    readonly productId: number | null;
  }>;
  readonly preferredWeightUnit: WeightUnit;
}): { readonly bestSizeKey: string; readonly bestPricePerItem: number | null } {
  // Get available products for the crop/blend
  // Get the target params: size, unit, price, product ID
  // Find best key, fallback to ounces?
  // If no key found then set price to empty?

  // If size is null then it means we have "trays" or "ounces" or "grams"
  // No need to check price or product ID or units
  if (inputSize == null) {
    return {
      bestSizeKey: createSizeKey({
        size: null,
        unit: inputUnit,
        pricePerItem: null,
        productId: null,
      }),
      bestPricePerItem: inputPricePerItem,
    };
  }

  // Check if this size key is available
  // If not then fall back to preferred weight unit with no price
  for (const { size, unit, pricePerItem, productId } of currentProducts) {
    if (size === inputSize && unit === inputUnit && productId === inputProductId) {
      return {
        bestSizeKey: createSizeKey({
          size: inputSize,
          unit: inputUnit,
          pricePerItem,
          productId,
        }),
        bestPricePerItem: inputPricePerItem,
      };
    }
  }

  for (const { size, unit, pricePerItem, productId } of currentProducts) {
    if (size === inputSize && unit === inputUnit) {
      return {
        bestSizeKey: createSizeKey({
          size: inputSize,
          unit: inputUnit,
          pricePerItem,
          productId,
        }),
        bestPricePerItem: inputPricePerItem,
      };
    }
  }

  // Fallback
  return {
    bestSizeKey: createSizeKey({
      size: null,
      unit: preferredWeightUnit,
      pricePerItem: null,
      productId: null,
    }),
    bestPricePerItem: inputPricePerItem,
  };
}

export const cropOrBlendFormsChanged = (
  existingCropFormValues: readonly CropOrBlendToUseFormInput[],
  newCropForms: readonly React.ReactElement<NewCropOrBlendToUseFormProps>[],
) => {
  const existingCropFormValuesSorted = existingCropFormValues.filter(
    (cropForm) => cropForm.cropOrBlendSelected !== undefined,
  );
  const newCropFormsSorted = newCropForms
    .map((cropForm) => cropForm.props.values)
    .filter(
      (cropForm) =>
        cropForm.cropOrBlendSelected !== undefined &&
        cropForm.itemCount !== '' &&
        Number.parseFloat(cropForm.itemCount) > 0 &&
        cropForm.itemCountValidation === '' &&
        cropForm.pricePerItemValidation === '',
    );

  if (existingCropFormValuesSorted.length !== newCropFormsSorted.length) {
    return true;
  }

  existingCropFormValuesSorted.sort(sortForms);
  newCropFormsSorted.sort(sortForms);

  for (let i = 0; i < existingCropFormValuesSorted.length; i++) {
    const existingValues = existingCropFormValuesSorted[i];
    const newValues = newCropFormsSorted[i];
    if (existingValues.cropOrBlendSelected === undefined || newValues.cropOrBlendSelected === undefined) {
      // This should never happen
      continue;
    }
    if (
      existingValues.cropOrBlendSelected.id !== newValues.cropOrBlendSelected.id ||
      Number.parseFloat(existingValues.itemCount) !== Number.parseFloat(newValues.itemCount) ||
      existingValues.countUnit !== newValues.countUnit ||
      existingValues.pricePerItem !== newValues.pricePerItem
    ) {
      return true;
    }
  }

  return false;
};

function sortForms(a: CropOrBlendToUseFormInput, b: CropOrBlendToUseFormInput) {
  if (a.cropOrBlendSelected === undefined || b.cropOrBlendSelected === undefined) {
    // This should never happen
    return 0;
  }
  if (a.cropOrBlendSelected.type === 'crop' && b.cropOrBlendSelected.type === 'crop') {
    return getNumericID(a.cropOrBlendSelected.id) - getNumericID(b.cropOrBlendSelected.id);
  }
  if (a.cropOrBlendSelected.type === 'blend' && b.cropOrBlendSelected.type === 'blend') {
    return getNumericID(a.cropOrBlendSelected.id) - getNumericID(b.cropOrBlendSelected.id);
  }

  return a.cropOrBlendSelected.type.localeCompare(b.cropOrBlendSelected.type);
}
